import {map} from 'lodash';
import {Fragment, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {BottomPopupDialog, Icon, Loader, TextArea, Carousel} from 'components';
import {usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_TEXT_CHAT.CHATBOT_HELP;

type Props = {
    open: boolean;
    loading: boolean;
    error?: boolean;
    suggestions: {text: string}[];
    onChange: (text: string) => void;
    onSubmit: () => void;
    onClose: () => void;
}

export const AiSuggestionsPopup: React.FC<Props> = ({open, loading, suggestions, error, onSubmit, onChange, onClose}) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);

    const [suggestionIndex, setSuggestionIndex] = useState(0);
    const [customSuggestionMessage, setCustomSuggestionMessage] = useState('');
    const [suggestionEditMode, setSuggestionEditMode] = useState(false);

    const handleEdit = useCallback(() => {
        setSuggestionEditMode(true);
        setCustomSuggestionMessage(suggestions?.[suggestionIndex]?.text);
    }, [suggestions, suggestionIndex]);

    const handleUpdateCustomMessage = useCallback((text: string) => {
        setCustomSuggestionMessage(text);
        onChange(text);
    }, [onChange]);

    useEffect(() => {
        if (open && !prevOpen) {
            setSuggestionEditMode(false);
            setCustomSuggestionMessage('');
            setSuggestionIndex(0);
        }
    }, [open, prevOpen]);

    useEffect(() => {
        if (open) {
            onChange(suggestions?.[suggestionIndex]?.text);
        }
    }, [open, onChange, suggestions, suggestionIndex]);

    return (
        <BottomPopupDialog open={open} title={t(T_KEYS.TITLE)} onClose={onClose}>
            <div>
                {error && !loading &&
                    <Fragment>
                        <div className='tw-h-32 tw-flex-center'>
                            <div className='tw-p-4 tw-bg-alert-light tw-shadow-sm tw-rounded-full'>
                                <Icon name='chatbot' size='4xl' color='light' thickness='xs' />
                            </div>
                        </div>
                        <div className='tw-mt-4 tw-mb-8 tw-text-center tw-text-lg'>{t(T_KEYS.ERROR)}</div>
                    </Fragment>}
                {loading && !error &&
                    <Fragment>
                        <div className='tw-h-32 tw-relative tw-flex-center tw-mt-8'>
                            <Loader className='tw-mx-auto' strokeWidth={2} colorClassName='tw-text-primary' />
                            <div className='tw-p-4 tw-bg-primary tw-shadow-sm tw-rounded-full'>
                                <Icon name='chatbot' size='4xl' color='light' thickness='xs' />
                            </div>
                        </div>
                        <div className='tw-my-8 tw-px-4 tw-text-center tw-text-lg'>{t(T_KEYS.LOADING)}</div>
                    </Fragment>}
                {!loading && !error && suggestions &&
                    <div className='tw-my-8'>
                        {!suggestionEditMode &&
                            <Carousel onUpdate={setSuggestionIndex}>
                                {map(suggestions, (suggestion, index) => (
                                    <div key={index} className='tw-min-h-24 tw-flex-center'>{suggestion.text}</div>
                                ))}
                            </Carousel>}
                        {suggestionEditMode &&
                            <TextArea rows={5} className='tw-mx-2' value={customSuggestionMessage} onChange={handleUpdateCustomMessage} />}
                        <div className='tw-flex tw-my-8'>
                            {!suggestionEditMode && <Icon name='edit' button onClick={handleEdit} />}
                            <Icon name='send' color='primary' button onClick={onSubmit} />
                        </div>
                    </div>}
            </div>
        </BottomPopupDialog>
    );
};